import { format, isEqual } from 'date-fns'

import { EMPTY_LIST, EMPTY_STRING } from '../../../constants/common'
import { CURRENCY_DEFAULT } from '../../../constants/currency'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../constants/date'
import { isEqualNullable } from '../../../utils/common'
import { parseAmount } from '../../../utils/number'

import {
  PROFILE_INFO_MONEY_EMPTY_AMOUNT,
  PROFILE_INFO_NOTES_FIELD_NAME,
  ProfileInfoFieldType,
} from './ProfileInfoWidget.constants'
import {
  getInfoDateValue,
  getInfoSelectLabel,
  isInfoCurrencyField,
  isInfoDateField,
  isInfoSelectField,
} from './ProfileInfoWidget.utils'

// A cleared amount is saved as zero, so it equals an initial `0.00`.
function toInfoMoneyAmountNumber(amount) {
  return amount == null || amount === EMPTY_STRING ? 0 : parseAmount(amount)
}

function isMoneyChanged({ current, initial }) {
  return (
    toInfoMoneyAmountNumber(current?.amount) !==
      toInfoMoneyAmountNumber(initial?.amount) ||
    current?.currency !== initial?.currency
  )
}

function getInfoFieldInitialValue({ optionLabelKey, rawValue, type }) {
  switch (type) {
    // The date input formats whatever it gets, so an empty string would crash it
    case ProfileInfoFieldType.DATE:
      return getInfoDateValue(rawValue)
    case ProfileInfoFieldType.SELECT:
      return rawValue?.id
        ? {
            label: getInfoSelectLabel({ optionLabelKey, value: rawValue }),
            value: rawValue.id,
          }
        : null
    case ProfileInfoFieldType.CURRENCY:
      return {
        amount:
          rawValue?.amount == null ? EMPTY_STRING : String(rawValue.amount),
        currency: rawValue?.currency ?? CURRENCY_DEFAULT,
      }
    default:
      return rawValue ?? EMPTY_STRING
  }
}

function toInfoDateApiValue(value) {
  return value ? format(value, DATE_API_DATA_FORMAT_SHORT) : null
}

export function focusInfoSelectSearchInputAfterOpen({ inputSelectRef }) {
  inputSelectRef.current?.focus()
}

export function toInfoSelectOptions(items, optionLabelKey = 'value') {
  return (items || EMPTY_LIST).map(item => ({
    label: item[optionLabelKey],
    value: item.id,
  }))
}

export function getInfoWidgetInitialValues({ config = EMPTY_LIST, data }) {
  return config.reduce(
    (values, { name, optionLabelKey, type }) => {
      values[name] = getInfoFieldInitialValue({
        optionLabelKey,
        rawValue: data?.[name],
        type,
      })

      return values
    },
    { [PROFILE_INFO_NOTES_FIELD_NAME]: data?.notes ?? EMPTY_STRING }
  )
}

export function getInfoWidgetPayload({
  blockName,
  config = EMPTY_LIST,
  initialValues,
  values,
}) {
  const changed = {}

  if (
    values[PROFILE_INFO_NOTES_FIELD_NAME] !==
    initialValues[PROFILE_INFO_NOTES_FIELD_NAME]
  ) {
    changed[PROFILE_INFO_NOTES_FIELD_NAME] =
      values[PROFILE_INFO_NOTES_FIELD_NAME]
  }

  config.forEach(({ name, type }) => {
    const current = values[name]
    const initial = initialValues[name]

    if (isInfoSelectField(type)) {
      const currentId = current?.value ?? null
      const initialId = initial?.value ?? null

      if (currentId !== initialId) {
        changed[name] = currentId
      }
    } else if (isInfoCurrencyField(type)) {
      const hasAmount =
        current?.amount !== EMPTY_STRING && current?.amount != null
      const hadAmount =
        initial?.amount !== EMPTY_STRING && initial?.amount != null

      if ((hasAmount || hadAmount) && isMoneyChanged({ current, initial })) {
        // The backend rejects a null amount, so a cleared field is saved as
        // zero, like the legacy profile form does.
        changed[name] = {
          amount: hasAmount
            ? String(current.amount)
            : PROFILE_INFO_MONEY_EMPTY_AMOUNT,
          currency: current?.currency ?? CURRENCY_DEFAULT,
        }
      }
    } else if (isInfoDateField(type)) {
      // `initialValues` is rebuilt on every render, so dates must be compared by
      // time and not by reference
      if (!isEqualNullable(current, initial, isEqual)) {
        changed[name] = toInfoDateApiValue(current)
      }
    } else if ((current ?? EMPTY_STRING) !== (initial ?? EMPTY_STRING)) {
      changed[name] = current
    }
  })

  if (Object.keys(changed).length === 0) {
    return null
  }

  // Flat data (no `block_name`, e.g. the provider profile) is PATCHed with the
  // changed fields directly; info blocks stay nested under their block name.
  return blockName ? { [blockName]: changed } : changed
}
